import React, { useState } from 'react';
import {
  IconX,
  IconExternalLink,
  IconRefresh,
  IconMailForward,
  IconFileInvoice,
  IconPencil,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useEsetSubscriptions } from '../../hooks/useEsetSubscriptions';
import {
  BILLING_CADENCE_MONTHLY,
  BILLING_CADENCE_ANNUAL,
  sendRenewalWarning,
  raiseRenewalInvoice,
  lineupLabel,
} from '../../services/esetApi';
import type { EsetSubscription } from '../../services/esetApi';
import { EsetProvisionSection } from './EsetProvisionSection';
import { EsetLicenseEdit } from './EsetLicenseEdit';
import './EsetManagePanel.css';

interface EsetManagePanelProps {
  open: boolean;
  onClose: () => void;
  /** The SM4RT customer's Dataverse GUID (the admin list is keyed on this). */
  customerGuid: string | null;
  /** The SM4RT customer's cr001_id (RepairShopr id) — provisioning is keyed on this. */
  customerId: string | null;
  customerName: string | null;
}

function cadenceLabel(value: number | null): string {
  if (value === BILLING_CADENCE_MONTHLY) return 'Monthly';
  if (value === BILLING_CADENCE_ANNUAL) return 'Annual';
  return '—';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function expiryText(sub: EsetSubscription): string {
  const days = sub.days_until_expiry;
  const on = formatDate(sub.expiry_date);
  if (days === null) return on === '—' ? 'No expiry set' : `Expires ${on}`;
  if (days < 0) return `Expired ${Math.abs(days)}d ago (${on})`;
  return `${days}d left · ${on}`;
}

export const EsetManagePanel: React.FC<EsetManagePanelProps> = ({
  open,
  onClose,
  customerGuid,
  customerId,
  customerName,
}) => {
  const { data, isLoading, error, refresh } = useEsetSubscriptions(customerGuid, open);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  if (!open) return null;

  const header = data?.customer ?? null;
  const subs = data?.subscriptions ?? [];

  const runAction = async (
    sub: EsetSubscription,
    action: 'warning' | 'invoice',
  ) => {
    setBusyId(`${sub.id}:${action}`);
    setNotice(null);
    try {
      const result =
        action === 'warning'
          ? await sendRenewalWarning(sub.id)
          : await raiseRenewalInvoice(sub.id);
      setNotice(`${action === 'warning' ? 'Warning' : 'Invoice'}: ${result.status ?? 'done'}`);
      refresh();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="eset-panel-overlay" role="dialog" aria-label="Manage ESET">
      <div className="eset-panel-overlay__scrim" onClick={onClose} />
      <div className="eset-panel">
        <div className="eset-panel__header">
          <span className="eset-panel__title">
            ESET
            {customerName && <span className="eset-panel__subtitle">{customerName}</span>}
          </span>
          <button className="eset-panel__icon-btn" onClick={refresh} aria-label="Refresh" title="Refresh">
            <IconRefresh size={15} stroke={1.5} />
          </button>
          <button className="eset-panel__close" onClick={onClose} aria-label="Close">
            <IconX size={16} stroke={1.5} />
          </button>
        </div>

        <div className="eset-panel__body">
          {notice && <div className="eset-panel__notice">{notice}</div>}
          {isLoading && <div className="eset-panel__muted">Loading ESET licenses…</div>}
          {error && <div className="eset-panel__error">{error}</div>}

          {!isLoading && !error && (
            <>
              {/* Account */}
              <div className="eset-card">
                <div className="eset-card__hdr">Account</div>
                <div className="eset-facts">
                  <div className="eset-fact">
                    <span className="eset-fact__label">Status</span>
                    <span className="eset-fact__value">{header?.status ?? '—'}</span>
                  </div>
                  <div className="eset-fact">
                    <span className="eset-fact__label">Billing</span>
                    <span className="eset-fact__value">{cadenceLabel(header?.billing_cadence ?? null)}</span>
                  </div>
                  <div className="eset-fact eset-fact--wide">
                    <span className="eset-fact__label">Installer</span>
                    <span className="eset-fact__value">
                      {header?.installer_url ? (
                        <a href={header.installer_url} target="_blank" rel="noreferrer" className="eset-link">
                          Download link <IconExternalLink size={12} stroke={1.5} />
                        </a>
                      ) : (
                        '—'
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Licenses */}
              <div className="eset-card">
                <div className="eset-card__hdr">
                  Licenses <span className="eset-card__count">{subs.length}</span>
                </div>
                {subs.length === 0 ? (
                  <div className="eset-panel__muted eset-panel__muted--pad">No ESET licenses yet.</div>
                ) : (
                  subs.map((sub) => (
                    <React.Fragment key={sub.id}>
                      <div className="eset-lic">
                        <div className="eset-lic__main">
                          <div className="eset-lic__name" title={sub.license_ref ?? undefined}>
                            {sub.commercial_product || sub.product_name || sub.license_ref || 'ESET license'}
                            {sub.billing_class ? ` · ${sub.billing_class}` : ''}
                          </div>
                          <div className="eset-lic__meta">
                            {expiryText(sub)}
                            {sub.quantity ? ` · ${sub.quantity} unit${sub.quantity === 1 ? '' : 's'}` : ''}
                            {sub.lineup && sub.lineup.length > 0
                              ? ` · ${lineupLabel(sub.lineup)}${sub.renewal_amount != null ? ` · $${sub.renewal_amount.toFixed(2)}/term` : ''}`
                              : ''}
                            {sub.lineup_error ? ` · ⚠ ${sub.lineup_error}` : ''}
                          </div>
                        </div>
                        <div className="eset-lic__badges">
                          {sub.trial && <span className="eset-badge eset-badge--info">Trial</span>}
                          {sub.cancelled && <span className="eset-badge eset-badge--neutral">Cancelled</span>}
                          {sub.suspended && <span className="eset-badge eset-badge--warn">Suspended</span>}
                          {sub.product_needs_review && (
                            <span
                              className="eset-badge eset-badge--warn"
                              title="A default product was set when the trial converted — confirm the product and class via Edit"
                            >
                              Review product
                            </span>
                          )}
                          {sub.seat_variance && (
                            <span
                              className="eset-badge eset-badge--warn eset-badge--seats"
                              title={`Seat variance — ESET licensed ${sub.licensed_units}, billed this term ${sub.billed_units ?? '?'} (usage ${sub.usage}). Extra units are charged pro rata; a drop applies at renewal.`}
                            >
                              <IconAlertTriangle size={11} stroke={1.5} /> {sub.licensed_units}/{sub.billed_units ?? '?'}
                            </span>
                          )}
                          {sub.warning_gap && (
                            <button
                              className="eset-badge-btn"
                              disabled={busyId === `${sub.id}:warning`}
                              onClick={() => runAction(sub, 'warning')}
                              title="Send the renewal warning email now"
                            >
                              <IconMailForward size={12} stroke={1.5} /> Warn
                            </button>
                          )}
                          {sub.invoice_gap && (
                            <button
                              className="eset-badge-btn"
                              disabled={busyId === `${sub.id}:invoice`}
                              onClick={() => runAction(sub, 'invoice')}
                              title="Raise the renewal invoice now"
                            >
                              <IconFileInvoice size={12} stroke={1.5} /> Invoice
                            </button>
                          )}
                          <button
                            className={`eset-lic__edit${editingId === sub.id ? ' eset-lic__edit--active' : ''}`}
                            onClick={() => setEditingId(editingId === sub.id ? null : sub.id)}
                            aria-label="Edit subscription"
                            title="Edit billing bundle / action on expiry"
                          >
                            <IconPencil size={14} stroke={1.5} />
                          </button>
                        </div>
                      </div>
                      {editingId === sub.id && (
                        <EsetLicenseEdit
                          sub={sub}
                          onCancel={() => setEditingId(null)}
                          onSaved={() => {
                            setEditingId(null);
                            setNotice('Subscription updated');
                            refresh();
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))
                )}
              </div>

              {/* Provisioning */}
              <EsetProvisionSection
                customerId={customerId}
                provisioned={Boolean(header?.vendor_account_id)}
                onChanged={refresh}
              />
            </>
          )}
        </div>

        <div className="eset-panel__footer">
          <button className="eset-panel__btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
